/**
 * Audit validation utilities for MetricData Dev Tool
 * Handles UUID detection, metric classification, and record count validation
 *
 * @module MetricDataDevTool/utils/auditValidators
 */

import {
  UUID_REGEX,
  LABOR_METRICS,
  SALES_METRICS,
  EXPECTED_RECORD_COUNTS
} from '../constants';

/**
 * Check if a metricId is in UUID format (legacy format)
 *
 * @param {string} metricId - The metricId to check
 * @returns {boolean} True if metricId is UUID format
 *
 * @example
 * isUuidFormat('550e8400-e29b-41d4-a716-446655440000') // true
 * isUuidFormat('store123#2025-01-01#netSales#daily') // false
 */
export function isUuidFormat(metricId) {
  if (!metricId || typeof metricId !== 'string') {
    return false;
  }
  return UUID_REGEX.test(metricId);
}

/**
 * Check if a metric is a labor metric
 *
 * @param {string} metricName - The metric name
 * @returns {boolean} True if labor metric
 *
 * @example
 * isLaborMetric('bohRegularHours') // true
 * isLaborMetric('netSales') // false
 */
export function isLaborMetric(metricName) {
  return LABOR_METRICS.includes(metricName);
}

/**
 * Check if a metric is a sales metric
 *
 * @param {string} metricName - The metric name
 * @returns {boolean} True if sales metric
 *
 * @example
 * isSalesMetric('netSales') // true
 * isSalesMetric('bohRegularHours') // false
 */
export function isSalesMetric(metricName) {
  return SALES_METRICS.includes(metricName);
}

/**
 * Get expected record count for a metric
 *
 * @param {string} metricName - The metric name
 * @returns {number} Expected count (2 for labor, 6 for sales, 0 for unknown)
 *
 * @example
 * getExpectedCount('bohRegularHours') // 2
 * getExpectedCount('netSales') // 6
 * getExpectedCount('unknownMetric') // 0
 */
export function getExpectedCount(metricName) {
  if (!metricName) return 0;

  if (isLaborMetric(metricName)) {
    return EXPECTED_RECORD_COUNTS.LABOR;
  }

  if (isSalesMetric(metricName)) {
    return EXPECTED_RECORD_COUNTS.SALES;
  }

  return 0; // Unknown metric
}

/**
 * Validate actual record count against expected count
 *
 * @param {string} metricName - The metric name
 * @param {number} actualCount - Actual number of records
 * @returns {Object} Validation result
 * @returns {string} return.metricName - The metric name
 * @returns {number} return.expected - Expected record count
 * @returns {number} return.actual - Actual record count
 * @returns {boolean} return.isValid - Whether count matches expected
 * @returns {number} return.variance - Difference (actual - expected)
 * @returns {string} return.metricType - Metric type ('labor', 'sales', 'unknown')
 *
 * @example
 * validateRecordCount('netSales', 6)
 * // {
 * //   metricName: 'netSales',
 * //   expected: 6,
 * //   actual: 6,
 * //   isValid: true,
 * //   variance: 0,
 * //   metricType: 'sales'
 * // }
 */
export function validateRecordCount(metricName, actualCount) {
  const expectedCount = getExpectedCount(metricName);

  let metricType = 'unknown';
  if (isLaborMetric(metricName)) {
    metricType = 'labor';
  } else if (isSalesMetric(metricName)) {
    metricType = 'sales';
  }

  return {
    metricName,
    expected: expectedCount,
    actual: actualCount,
    isValid: actualCount === expectedCount,
    variance: actualCount - expectedCount,
    metricType
  };
}
